// Config panel for the "ivr_menu" action block
// IVR menu builder: greeting, DTMF options, timeout action
import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'];

const LANGUAGES = [
  { value: '', label: 'Auto-detect (from resident)' },
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'fr', label: 'Fran\u00e7ais' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Espa\u00f1ol' },
];

const ACTIONS = [
  { value: 'redirect', label: 'Route to Extension' },
  { value: 'trunk_dial', label: 'Dial External Number' },
  { value: 'voicemail', label: 'Send to Voicemail' },
  { value: 'reject', label: 'Reject Call' },
];

const IvrMenuConfig = ({ config, onChange, onSummaryChange }) => {
  const options = config.options || [];
  const timeoutAction = config.timeoutAction || { action: 'voicemail', targets: '' };

  const updateConfig = (updates) => {
    const updated = { ...config, ...updates };
    onChange(updated);
    const optCount = (updated.options || []).length;
    const greeting = updated.greeting || 'default';
    onSummaryChange(`IVR: ${greeting} (${optCount} options)`);
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = options.map((opt, i) =>
      i === index ? { ...opt, [field]: value } : opt
    );
    updateConfig({ options: newOptions });
  };

  const addOption = () => {
    // Find first unused digit
    const usedDigits = new Set(options.map(o => o.digit));
    const nextDigit = DIGITS.find(d => !usedDigits.has(d)) || '1';
    updateConfig({
      options: [...options, { digit: nextDigit, label: '', action: 'redirect', targets: '', timeout: 30, mode: 'single' }]
    });
  };

  const removeOption = (index) => {
    updateConfig({ options: options.filter((_, i) => i !== index) });
  };

  const handleTimeoutAction = (field, value) => {
    updateConfig({ timeoutAction: { ...timeoutAction, [field]: value } });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Play an audio greeting, then wait for the caller to press a digit.
        Each digit maps to a routing action.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          label="Greeting Audio File"
          value={config.greeting || ''}
          onChange={(e) => updateConfig({ greeting: e.target.value })}
          required
          placeholder="welcome-menu"
          helperText="Asterisk sound file (without extension)"
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          select
          label="Language"
          value={config.language || ''}
          onChange={(e) => updateConfig({ language: e.target.value })}
          sx={{ width: 200 }}
          helperText="Audio language"
        >
          {LANGUAGES.map(l => (
            <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          label="Invalid Input Audio"
          value={config.invalidAudio || ''}
          onChange={(e) => updateConfig({ invalidAudio: e.target.value })}
          placeholder="invalid"
          helperText="Played when caller presses an unassigned digit"
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="Timeout"
          type="number"
          value={config.timeout ?? 10}
          onChange={(e) => updateConfig({ timeout: Math.min(30, Math.max(3, parseInt(e.target.value) || 10)) })}
          inputProps={{ min: 3, max: 30 }}
          sx={{ width: 90 }}
          helperText="sec"
        />
        <TextField
          size="small"
          label="Retries"
          type="number"
          value={config.maxRetries ?? 3}
          onChange={(e) => updateConfig({ maxRetries: Math.min(5, Math.max(1, parseInt(e.target.value) || 3)) })}
          inputProps={{ min: 1, max: 5 }}
          sx={{ width: 90 }}
          helperText="max"
        />
      </Box>

      <Divider />

      <Typography variant="caption" fontWeight={600}>
        Menu Options
      </Typography>

      {options.map((opt, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            select
            label="Key"
            value={opt.digit || '1'}
            onChange={(e) => handleOptionChange(index, 'digit', e.target.value)}
            sx={{ width: 65 }}
          >
            {DIGITS.map(d => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Label"
            value={opt.label || ''}
            onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
            placeholder="Reception"
            sx={{ flex: 1, minWidth: 100 }}
          />
          <TextField
            size="small"
            select
            label="Action"
            value={opt.action || 'redirect'}
            onChange={(e) => handleOptionChange(index, 'action', e.target.value)}
            sx={{ width: 160 }}
          >
            {ACTIONS.map(a => (
              <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
            ))}
          </TextField>
          {opt.action !== 'reject' && (
            <TextField
              size="small"
              label="Target"
              value={opt.targets || ''}
              onChange={(e) => handleOptionChange(index, 'targets', e.target.value)}
              placeholder={opt.action === 'trunk_dial' ? '+3241234...' : '6001'}
              sx={{ flex: 1, minWidth: 100 }}
            />
          )}
          {(opt.action === 'redirect' || opt.action === 'trunk_dial') && (
            <TextField
              size="small"
              label="Timeout"
              type="number"
              value={opt.timeout ?? 30}
              onChange={(e) => handleOptionChange(index, 'timeout', Math.min(120, Math.max(5, parseInt(e.target.value) || 30)))}
              inputProps={{ min: 5, max: 120 }}
              sx={{ width: 80 }}
            />
          )}
          <IconButton size="small" onClick={() => removeOption(index)} sx={{ mt: 0.5 }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      {options.length < 12 && (
        <Box>
          <IconButton size="small" onClick={addOption} color="primary">
            <AddIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" color="text.secondary">Add menu option</Typography>
        </Box>
      )}

      <Divider />

      <Typography variant="caption" fontWeight={600}>
        Timeout / Exhausted Action
      </Typography>
      <Typography variant="caption" color="text.secondary">
        What happens after the caller fails to press a valid digit within the retry limit.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          select
          label="Action"
          value={timeoutAction.action || 'voicemail'}
          onChange={(e) => handleTimeoutAction('action', e.target.value)}
          sx={{ width: 160 }}
        >
          {ACTIONS.filter(a => a.value !== 'trunk_dial').map(a => (
            <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
          ))}
        </TextField>
        {timeoutAction.action !== 'reject' && (
          <TextField
            size="small"
            label="Target"
            value={timeoutAction.targets || ''}
            onChange={(e) => handleTimeoutAction('targets', e.target.value)}
            placeholder="6001"
            sx={{ flex: 1 }}
          />
        )}
      </Box>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
        The greeting audio is played, then the system waits for 1 DTMF digit.
        If the digit matches an option, that action executes.
        If invalid or no input, the greeting replays up to the retry limit.
        <strong>Language support:</strong> Audio is resolved per language automatically.
        Asterisk looks in <code>/var/lib/asterisk/sounds/&lt;language&gt;/</code> first
        (e.g. <code>nl/</code> for Dutch), then falls back to the default directory.
        Ensure the same filename exists for each supported language.
      </Alert>
    </Box>
  );
};

export default IvrMenuConfig;
